// WbHaulierSearch — server-side debounced haulier typeahead.
//
// Replaces the previous load-once-and-filter-client-side dropdown used
// on the vehicle and driver detail pages. The old approach capped at
// per_page=100 and silently truncated for tenants with more than 100
// hauliers — see WEIGHBRIDGE_FLAGS_AND_CONCERNS.md flag C4. This
// component queries `/wb/hauliers?name=<q>&per_page=20` on each
// debounced keystroke and shows the matches as a dropdown.
//
// Props mirror the pattern WbCustomerSearch establishes: a single
// `value: { enc_id, name } | null` plus an `onChange` that fires when
// the user picks a result (or clears the field).

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { useWbHaulierSearch } from '../hooks/useWbHaulierSearch';
import styles from './WbHaulierSearch.module.css';

export interface WbHaulierSearchValue {
  enc_id: string;
  name: string;
}

export interface WbHaulierSearchProps {
  value: WbHaulierSearchValue | null;
  onChange: (next: WbHaulierSearchValue | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Optional id passed through to the <input> for label association. */
  id?: string;
  ariaLabel?: string;
}

export function WbHaulierSearch({
  value,
  onChange,
  disabled = false,
  placeholder = 'Search hauliers…',
  id,
  ariaLabel = 'Haulier',
}: WbHaulierSearchProps): JSX.Element {
  // `text` is what the user is typing right now. `debounced` is what we
  // fire the network request against.
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  // 250 ms debounce — typing fast shouldn't fire one request per keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebounced(text.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [text]);

  const search = useWbHaulierSearch(debounced);
  const results = search.data?.hauliers ?? [];

  // Close the dropdown on outside clicks.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    function handle(e: MouseEvent) {
      if (!rootRef.current) return;
      if (e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleSelect(h: { enc_id: string; name: string }) {
    onChange({ enc_id: h.enc_id, name: h.name });
    setText('');
    setDebounced('');
    setOpen(false);
  }

  function handleClear() {
    onChange(null);
    setText('');
    setDebounced('');
    setOpen(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      const first = results[0];
      if (first) handleSelect(first);
    }
  }

  // When a haulier is already selected, show its name in the input and
  // suppress the typeahead unless the user clears it. This mirrors the
  // "chip-style" picker pattern used elsewhere.
  const displayValue = value !== null && text === '' ? value.name : text;

  return (
    <div ref={rootRef} className={styles.root} data-testid="wb-haulier-search">
      <div className={styles.inputRow}>
        <input
          id={id}
          className={styles.input}
          type="search"
          value={displayValue}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            // Typing into the field while a value is selected clears
            // the selection — the user has indicated intent to re-pick.
            if (value !== null) onChange(null);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          autoComplete="off"
        />
        {value !== null ? (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            disabled={disabled}
            aria-label="Clear haulier"
          >
            Clear
          </button>
        ) : null}
      </div>

      {open && debounced.length > 0 ? (
        <div className={styles.dropdown} role="listbox">
          {search.isLoading ? (
            <div className={styles.dropdownStatus}>Searching…</div>
          ) : search.isError ? (
            <div className={styles.dropdownStatus}>Couldn't load hauliers.</div>
          ) : results.length === 0 ? (
            <div className={styles.dropdownStatus}>No hauliers match.</div>
          ) : (
            results.map((h) => (
              <button
                key={h.enc_id}
                type="button"
                role="option"
                aria-selected={value?.enc_id === h.enc_id}
                className={styles.option}
                onClick={() => handleSelect(h)}
              >
                {h.name}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
